import { __decorate } from "tslib";
import { List, is } from "immutable";
import { LitElement, css, html } from "lit";
import { property, state } from "lit/decorators.js";
import { createRef, ref } from "lit/directives/ref.js";
import { repeat } from "lit/directives/repeat.js";
import { lxlCustomElement } from "../lxl-custom-element";
import modalC from "./lxl-modal-controller";
/**
 * A component that manages the rendering of Lit templates given by the [[ModalController]].
 * It should be placed at the end of the `<body>` tag in you application like so:
 *
 * ```html
 *  <!DOCTYPE html>
 *  <html>
 *    <head>
 *      <!-- meta, scripts, etc. -->
 *    </head>
 *    <body>
 *      <my-lit-app></my-lit-app>
 *      <modal-portal></modal-portal>
 *    </body>
 *  </html>
 * ```
 *
 * This element listens to the `"removeModal"` event.
 * Upon receiving the event, it inspects the event's `composedPath()` the determine
 * which modal the event came from and removes that one.
 */
let LxlModalPortal = class LxlModalPortal extends LitElement {
    /**
     * A list of the modals currently present in the DOM. Used by the [[ModalController]].
     */
    get modalNodes() {
        var _a;
        return (_a = this.portalRef.value) === null || _a === void 0 ? void 0 : _a.children;
    }
    constructor() {
        super();
        this["z-index"] = 9999;
        this.modalStack = List();
        this.portalRef = createRef();
        /**
         * Inspects the `composedPath()` of the given event and removes the modal that
         * intersects with the path, if any.
         */
        this.removeModal = (e) => {
            e.stopImmediatePropagation();
            e.preventDefault();
            // Locate portal in event path and grab child modal-node.
            const eventPath = e.composedPath();
            const portalEventPathIndex = eventPath.findIndex((el) => el === this.portalRef.value);
            if (portalEventPathIndex < 1) {
                console.warn("Could not locate modal portal at appropriate depth in the @removeModal event path");
            }
            else {
                const modalNode = eventPath[portalEventPathIndex - 1];
                modalC.removeByNode(modalNode);
            }
        };
        modalC.attach(this);
    }
    /**
     * Used by the [[ModalController]] when a modal is added or removed.
     * Updates the `<body>` element to have the `"modal-portal-active"` class
     * precisely when the `<modal-portal>` contains at least one modal.
     */
    offerState(newState) {
        if (!is(this.modalStack, newState.get("modalStack"))) {
            this.modalStack = newState.get("modalStack");
            if (this.modalStack.size > 0) {
                document.querySelector("body").classList.add("modal-portal-active");
            }
            else {
                document.querySelector("body").classList.remove("modal-portal-active");
            }
        }
    }
    connectedCallback() {
        super.connectedCallback();
        this.addEventListener("removeModal", this.removeModal);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        modalC.disconnect(this);
    }
    updated(changedProperties) {
        super.updated(changedProperties);
        this.setAttribute('tabindex', '-1');
    }
    render() {
        var _a;
        return html `
      <div id="portal" ${ref(this.portalRef)} tabindex="-1">
        ${repeat((_a = this.modalStack) === null || _a === void 0 ? void 0 : _a.values(), (modal) => modal.key, (modal) => html `<div class="modal-node">${modal}</div>`)}
      </div>
    `;
    }
};
LxlModalPortal.styles = css `
    :host {
      position: fixed;
      z-index: 9999;
      top: 0;
      left: 0;
    }

    #portal {
      isolation: isolate;
    }
  `;
__decorate([
    property()
], LxlModalPortal.prototype, "z-index", void 0);
__decorate([
    state()
], LxlModalPortal.prototype, "modalStack", void 0);
LxlModalPortal = __decorate([
    lxlCustomElement("lxl-modal-portal")
], LxlModalPortal);
export default LxlModalPortal;
//# sourceMappingURL=lxl-modal-portal.js.map